import { type CardDetails } from '@getbento/payments/types/clover';
import { COMMON_DATETIME_FORMATS } from 'reservations/base/helpers/datetime';
import { getDateTime } from 'reservations/base/helpers/getDateTime';
import { type BookingType } from 'reservations/booking/types';

class FormBuilder {
  formData: Map<any, any>;

  constructor() {
    this.formData = new Map();

    // NOTE: These are mandatory for now, but may be optional in the future
    // TODO: Remove once we've fully migrated to V2 as these will be set by the backend
    this.formData.set('optInEmail', true);
    this.formData.set('optInSms', true);
  }

  addName(name: string): FormBuilder {
    if (!name) {
      throw new Error('Invalid name is provided');
    }

    const nameParts = name.split(' ');

    if (nameParts.length === 1) {
      this.formData.set('firstName', name);
      this.formData.set('lastName', '');

      return this;
    }

    const lastName = nameParts[nameParts.length - 1];
    const firstName = nameParts.slice(0, nameParts.length - 1).join(' ');

    this.formData.set('firstName', firstName);
    this.formData.set('lastName', lastName);

    return this;
  }

  addSpecialOccasion(specialOccasion: string): FormBuilder {
    if (!specialOccasion) {
      throw new Error('Invalid specialOccasion is provided');
    }

    if (specialOccasion !== 'None') {
      this.formData.set('tags', [specialOccasion.toLowerCase()]);
    }

    return this;
  }

  addPartySize(partySize: number | string): FormBuilder {
    if (!partySize) {
      throw new Error('Invalid partySize provided');
    }

    this.formData.set('partySize', partySize.toString());

    return this;
  }

  addEmail(email: string): FormBuilder {
    if (!email) {
      throw new Error('Invalid email is provided');
    }

    this.formData.set('email', email);

    return this;
  }

  addPhoneNumber(phone: string, phoneCode: string): FormBuilder {
    if (!phone || !phoneCode) {
      throw new Error('Invalid phone number is provided');
    }

    this.formData.set('phone', phone);
    this.formData.set('phoneCode', phoneCode);

    return this;
  }

  addDateTime(
    date: Date,
    availability?: string | null,
    bookingType?: BookingType
  ): FormBuilder {
    if (bookingType === 'Waitparty') {
      return this;
    }

    if (availability == null || availability === '') {
      throw new Error('Invalid availability is provided');
    }

    date = getDateTime(date, availability);

    const formattedDateString = COMMON_DATETIME_FORMATS.iso(date);

    this.formData.set('dateAndTime', formattedDateString);

    return this;
  }

  addNote(note: string): FormBuilder {
    this.formData.set('note', note);

    return this;
  }

  addMarketingOptIn(
    optInMarketingEmail: boolean,
    optInMarketingSms: boolean
  ): FormBuilder {
    this.formData.set('optInMarketingEmail', optInMarketingEmail);
    this.formData.set('optInMarketingSms', optInMarketingSms);

    return this;
  }

  addBookingType(bookingType: BookingType): FormBuilder {
    this.formData.set('bookingType', bookingType);
    return this;
  }

  addPaymentMethod(
    savedPaymentMethod: string,
    token: string,
    cardDetails: CardDetails | null
  ): FormBuilder {
    if (token) {
      this.formData.set('token', token);

      if (cardDetails) {
        this.formData.set('cardDetails', cardDetails);
      }
    } else if (savedPaymentMethod) {
      this.formData.set('savedPaymentMethod', savedPaymentMethod);
    }

    return this;
  }

  addChecklessPayments(tipPercentage: number): FormBuilder {
    this.formData.set('tipPercentage', tipPercentage);

    return this;
  }

  addLocation(locationId: string): FormBuilder {
    if (!locationId) {
      throw new Error('No location id provided');
    }

    this.formData.set('locationId', locationId);
    return this;
  }

  addGuests(guests: []): FormBuilder {
    this.formData.set('guests', guests);

    return this;
  }

  build(): Map<string, string | string[]> {
    return this.formData;
  }
}

export { FormBuilder };
